import {
  useQuery,
} from '@tanstack/react-query';
import { 
  getAttendanceHistoryV2, 
  getAttendanceStatusV2, 
  getStoreAttendanceReportV2 
} from '../../services/attendance.service';
import {
  IAttendanceHistory,
  IAttendanceStatus,
  IAttendanceReport,
  IAttendanceHistoryParams,
  IAttendanceStatusParams,
  IStoreAttendanceReportParams
} from './interfaces';

export const useGetAttendanceHistory = (
  enabled: boolean, 
  params: IAttendanceHistoryParams
) => useQuery<IAttendanceHistory>({
  queryKey: ['attendance', 'history', params],
  enabled: enabled && !!params.userId, // Requiere userId
  queryFn: async ({ queryKey }) => {
    try {
      const [_key, _type, params] = queryKey;
      const res = await getAttendanceHistoryV2(params as IAttendanceHistoryParams);
      if (!res?.data) {
        throw new Error('Oh no! Something went wrong while fetching attendance history.');
      }
      return res.data;
    } catch (e: any) {
      console.log('error caught', e['response']?.data?.['errors']);
      throw e;
    }
  }
});

export const useGetAttendanceStatus = (
  enabled: boolean, 
  params: IAttendanceStatusParams
) => useQuery<IAttendanceStatus>({
  queryKey: ['attendance', 'status', params],
  enabled: enabled && !!params.userId, // Requiere userId
  queryFn: async ({ queryKey }) => {
    try {
      const [_key, _type, params] = queryKey;
      const res = await getAttendanceStatusV2(params as IAttendanceStatusParams);
      if (!res?.data) {
        throw new Error('Oh no! Something went wrong while fetching attendance status.');
      }
      return res.data;
    } catch (e: any) {
      console.log('error caught', e['response']?.data?.['errors']);
      throw e;
    }
  }
});

export const useGetStoreAttendanceReport = (
  enabled: boolean, 
  storeId: string, 
  params: IStoreAttendanceReportParams
) => useQuery<IAttendanceReport>({
  queryKey: ['attendance', 'store-report', { storeId, ...params }],
  enabled: enabled && !!storeId,
  queryFn: async ({ queryKey }) => {
    try {
      const [_key, _type, queryParams] = queryKey;
      const { storeId, ...reportParams } = queryParams as { storeId: string } & IStoreAttendanceReportParams;
      const res = await getStoreAttendanceReportV2(storeId, reportParams);
      if (!res?.data) {
        throw new Error('Oh no! Something went wrong while fetching store attendance report.');
      }
      return res.data;
    } catch (e: any) {
      console.log('error caught', e['response']?.data?.['errors']);
      throw e;
    }
  }
});

// Hook para obtener el estado con polling cada hora (perfecto para turnos largos)
export const useGetAttendanceStatusHourly = (
  enabled: boolean, 
  params: IAttendanceStatusParams
) => useQuery<IAttendanceStatus>({
  queryKey: ['attendance', 'status', 'hourly', params],
  enabled: enabled && !!params.userId, // Requiere userId
  refetchInterval: 3600000, // 1 hora (3,600,000 ms)
  refetchIntervalInBackground: false,
  retry: (failureCount, error) => {
    console.log('retry', failureCount, error);
    return failureCount < 3;
  },
  
  // Eventos más útiles que polling frecuente
  refetchOnWindowFocus: true,
  refetchOnReconnect: true,
  
  // Optimización de cache - datos frescos por 30 minutos
  staleTime: 1800000, // 30 minutos
  
  queryFn: async ({ queryKey }) => {
    try {
      const [_key, _type, _subtype, params] = queryKey;
      const res = await getAttendanceStatusV2(params as IAttendanceStatusParams);
      if (!res?.data) {
        throw new Error('Oh no! Something went wrong while fetching attendance status.');
      }
      return res.data?.data;
    } catch (e: any) {
      console.log('error caught', e['response']?.data?.['errors']);
      throw e;
    }
  }
}); 